import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";

interface ClicksSplitTooltipProps {
    percentAdClicksFromSearch?: number;
    percentAdClicksFromShopping?: number;
    percentOrganicClicks?: number;
    onMouseLeave?: () => void;
}

export default function ClicksSplitTooltip({
    percentAdClicksFromSearch = 0,
    percentAdClicksFromShopping = 0,
    percentOrganicClicks = 0,
    onMouseLeave = () => {},
}: ClicksSplitTooltipProps) {
    const theme = useTheme();
    
    // Calculate percentages
    const adClicksPercent = Math.min(Math.max(percentAdClicksFromSearch, 0), 100);
    const shoppingClicksPercent = Math.min(Math.max(percentAdClicksFromShopping, 0), 100);
    const organicClicksPercent = Math.min(Math.max(percentOrganicClicks, 0), 100);
    
    // Calculate total for bar visualization
    const total = adClicksPercent + shoppingClicksPercent + organicClicksPercent;
    const adClicksBarWidth = total > 0 ? (adClicksPercent / total) * 100 : 0;
    const shoppingClicksBarWidth = total > 0 ? (shoppingClicksPercent / total) * 100 : 0;
    const organicClicksBarWidth = total > 0 ? (organicClicksPercent / total) * 100 : 0;

    return (
        <Box
            sx={{
                background: `linear-gradient(to right, ${theme.palette.grey[800]}, ${theme.palette.grey[800]})`,
                border: `1px solid ${alpha(theme.palette.grey[600], 0.3)}`,
                borderRadius: '12px',
                overflow: 'hidden',
                minWidth: 250,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Box
                sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                    px: 2,
                    py: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: 1,
                        color: theme.palette.common.white,
                    }}
                >
                    Clicks split
                </Typography>
            </Box>

            {/* Bar Chart */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                }}
            >
                <Stack direction="row" spacing={0.5} sx={{ width: '100%', height: 12 }}>
                    {/* Ad Clicks segment (green) */}
                    {adClicksBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: adClicksBarWidth,
                                height: 12,
                                backgroundColor: '#18A155',
                                borderRadius: '2px',
                                minWidth: adClicksBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* Shopping Clicks segment (orange/gold) */}
                    {shoppingClicksBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: shoppingClicksBarWidth,
                                height: 12,
                                backgroundColor: '#BC9510',
                                borderRadius: '2px',
                                minWidth: shoppingClicksBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* Organic Clicks segment (yellow) */}
                    {organicClicksBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: organicClicksBarWidth,
                                height: 12,
                                backgroundColor: '#F5CE47',
                                borderRadius: '2px',
                                minWidth: organicClicksBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                </Stack>
            </Box>

            {/* Legend */}
            <Box
                sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.03),
                    px: 2,
                    py: 2,
                }}
            >
                <Stack spacing={1.5}>
                    {/* Ad Clicks */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#18A155',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                Ad Clicks
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {fPercent(adClicksPercent)}
                        </Typography>
                    </Stack>

                    {/* Shopping Clicks */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#BC9510',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                Shopping Clicks
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {fPercent(shoppingClicksPercent)}
                        </Typography>
                    </Stack>

                    {/* Organic Clicks */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#F5CE47',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                Organic Clicks
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {fPercent(organicClicksPercent)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}

